import React, { useState, useEffect } from 'react';
import { Tr, Td, Text, Badge, Flex, Box, Tooltip } from '@chakra-ui/react';
import api from '../../services/api';
import { formatUserEmail, formatRelativeTime } from './TasksUtils';
import {
  StatusCell,
  IdCell,
  BrideGroomCell,
  AssignedCell,
  FollowUpCell,
  TaskDateCell,
  TitleCell,
  PriorityCell,
  TaskAssigneeCell,
  DueDateCell,
} from './TasksCellRenderers';
import { TaskActions, StatusToggle } from './TasksRowActions';

/**
 * Component for rendering a single task row
 */
const TasksSingleRow = ({ task, onStatusChange, onTitleChange }) => {
  const [users, setUsers] = useState([]);
  const [leadAssignedUser, setLeadAssignedUser] = useState(null);
  
  // Fetch all users to map UIDs to emails
  useEffect(() => {
    let isMounted = true;
    
    const fetchUsers = async () => {
      try {
        const response = await api.get('/api/users');
        if (isMounted && response.data && response.data.users) {
          setUsers(response.data.users);
          
          // If we have a lead with assignedTo as UID, find the corresponding user
          if (task.leadDetails?.assignedTo) {
            const assignedUser = response.data.users.find(user => user.uid === task.leadDetails.assignedTo);
            if (assignedUser) {
              setLeadAssignedUser(assignedUser);
            }
          }
        }
      } catch (error) {
        console.error('[TasksSingleRow] Error fetching users:', error);
      }
    };
    
    fetchUsers();
    
    return () => {
      isMounted = false;
    };
  }, [task.leadDetails?.assignedTo]);
  
  return (
    <>
      {/* Single task header - consistent with group header */}
      <Tr bg="gray.50" className="group-header">
        <Td colSpan={11} py={1} px={2}>
          <Flex align="center">
            <TaskActions task={task} isHeader={true} />
            <Flex mr={2} alignItems="center">
              <Text fontWeight="medium" mr={1}>{task.leadId || 'No ID'}</Text>
              <Tooltip label={task.leadDetails?.brideAndGroomName || "Unknown"}>
                <Text fontWeight="medium" minW="130px" maxW="240px" w="240px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                  {task.leadDetails?.brideAndGroomName || "Unknown"}
                </Text>
              </Tooltip>
              <Badge ml={2} colorScheme="blue">1 task</Badge>
            </Flex>
            {task.leadDetails?.nextFollowUpTime && (
              <Text fontSize="xs" color="orange.500" fontWeight="medium" mx={2}>
                Follow Up: {new Date(task.leadDetails.nextFollowUpTime) > new Date() ? 'in ' : ''}
                {formatRelativeTime(task.leadDetails.nextFollowUpTime)}
              </Text>
            )}
            <Text fontSize="xs" color="gray.600" mx={4}>
              Assigned to: {task.assignedTo?.email ? formatUserEmail(task.assignedTo.email) : "Unassigned"}
            </Text>
          </Flex>
        </Td>
      </Tr>
      
      {/* Task row with same styling as grouped tasks, plus 100px left and right padding */}
      <Tr 
        className="grouped-task-row"
        _hover={{ bg: 'gray.100' }}
        borderLeftWidth="4px"
        borderLeftColor="blue.200"
        borderLeftStyle="solid"
        sx={{ '& > td:first-of-type': { pl: '100px' }, '& > td:last-of-type': { pr: '100px' } }}
      >
        {/* Status toggle */}
        <StatusToggle task={task} onStatusChange={onStatusChange} />

      {/* Task Title with edit capability */}
      <TitleCell task={task} onTitleChange={onTitleChange} />
      
      {/* Priority */}
      <PriorityCell task={task} />
      
      {/* Task Assignee with edit capability */}
      <TaskAssigneeCell task={task} users={users} onStatusChange={onStatusChange} />
      
      {/* Due Date with edit capability */}
      <DueDateCell task={task} onStatusChange={onStatusChange} />
      
      {/* Empty cell for actions since they're in the header */}
      <Td py="1" px="0"></Td>
    </Tr>
    
    {/* Optional visual separator after the single task */}
    <Tr className="group-separator">
      <Td colSpan={11} py={0} px={0}>
        <Box h="4px" bg="white"></Box>
      </Td>
    </Tr>
    </>
  );
};

export default TasksSingleRow;
